
import { Component, Vue, Emit, Watch, Prop } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { vuex } from "@/store";
import rest from "@/rest";
import { signalrEventBus } from "@/main";
// components
import MarkdownEditor from "../Shared/MarkdownEditor.vue";
// interfaces
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { CourseElementVm } from "@/interfaces/CourseElementVm";
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { CourseElementType } from "@/interfaces/CourseElementType";
import { courseElementTypeItems } from "@/interfaces/Types";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { TtsVoiceVm } from "@/interfaces/CustomizationSelectionsVm";
import TtsVoiceSelection from "./TtsVoiceSelection.vue";
import { ExBundleVM } from "@/interfaces/ExBundleVM";

const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    MarkdownEditor,
    TtsVoiceSelection
  },
})
export default class TtsAudioElementSettings extends Vue {
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedBundle!: ExBundleVM;
  @globals.Getter selectedElement!: CourseElementVm;
  @globals.Getter selectedExercise!: ExerciseVM | null;
  @globals.Getter selectedLanguageCode!: string | null;
  @globals.Action refreshSelectedElement!: () => Promise<void>;
  courseElementTypeItems = courseElementTypeItems;

  isBusy = false;
  selectedAudioLanguageCode = "de-DE";
  selectedVoiceLanguage: string = "";
  selectedVoiceId: string = "";
  isBusyOnTranscription = false;
  showMarkdownEditor = false;
  id = "";

  mounted() {
    // if (!this.selectedElement || !this.selectedBundle) return;
    if (!this.selectedBundle.sequentialModuleProcessing) {
      this.selectedElement.preventSeeking = false;
    }
  }

  created() {
    // SignalR event listener
    signalrEventBus.$on('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  beforeDestroy() {
    // clearAllBodyScrollLocks();
    // Make sure to cleanup SignalR event handlers when removing the component
    signalrEventBus.$off('backgroundTaskEvent', this.backgroundTaskEvent);
  }

  // SignalR events
  async backgroundTaskEvent(response: { id: string, progress: number, message: string, status: string }) {
    if (this.id !== response.id) return;
    console.log("TtsAudioElementSettings > Background task event:", response);

    if (response.status == "INF") {
      vuex.ux.SB_SUCCESS({
        message: response.message,
        timeout: 1500
      });
      return;
    }

    // Reload the element to get the updated AI summary
    await this.refreshSelectedElement();
    // Update MediaPool
    this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list

    this.isBusyOnTranscription = false;
  }

  get fileName(): string {
    if (!this.selectedElement || !this.selectedElement.contentUrl) return "";
    return this.$globalHelper.extractFileName(this.selectedElement.contentUrl);
  }

  async onCreateAiSummary(): Promise<void> {
    if (!this.selectedElement || !this.selectedBundle) return;

    await this.$l3rnOnlineHub.ensureConnection();
    this.isBusyOnTranscription = true;

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["SignalRId"] = this.$l3rnOnlineHub.ConnectionId!;
    params.dictionary!["BundleId"] = this.selectedBundle.id!.toString();
    params.dictionary!["FileName"] = this.$globalHelper.extractFileName(this.selectedElement.contentUrl ?? "");
    params.dictionary!["LanguageCode"] = this.selectedAudioLanguageCode || "de-DE";

    await rest.url("contentManager/createAiSummary").post(params)
      .then(async (response) => {
        console.log("TtsAudioElementSettings > Create AI Summary response:", response);
        if (!response || !this.selectedElement || !this.selectedBundle) return;
        this.id = response;
      })
      .catch(err => {
      })
  }

  // TTS Audio handler
  async onGenerateTtsAudio(): Promise<void> {
    if (!this.selectedExercise || !this.selectedElement|| !this.selectedElement.text) return;
    this.isBusy = true;
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["OldFilePath"] = this.selectedElement.contentUrl ?? "";
    params.dictionary!["BundleId"] = this.selectedExercise.exBundleId!.toString();
    params.dictionary!["ExerciseId"] = this.selectedExercise.id!.toString();
    params.dictionary!["Text"] = this.selectedElement.text!;
    params.dictionary!["LanguageCode"] = this.selectedVoiceLanguage ?? "de-DE";
    params.dictionary!["CcLanguageCode"] = this.selectedLanguageCode ?? "de-DE";
    params.dictionary!["Voice"] = this.selectedVoiceId ?? "";

    // Clear content url to force audio player to update on new url is received
    this.selectedElement.contentUrl = null;

    const response = await rest.url("contentManager/convertTextToSpeech").post(params)
    //const response = await rest.url("contentManager/createPodcastEpisode").post(params)
      .then(async (response) => {
        if (!response || !this.selectedElement) return;
        console.log("TtsAudioElementSettings > Convert Text to Speech response:", response);
        this.selectedElement.contentUrl = response;
        // this.selectedElement.createdWithAi = true;
        this.selectedElement.aiSummary = ""; // reset AI summary when new audio is generated
        this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list
      })
      .catch(err => {
      })
      .finally(() => {
        this.isBusy  = false;
      });
  }

  getElementTitle(type: string | null): string {
    const element = this.courseElementTypeItems.find(el => CourseElementType[el.value] === type);
    return element ? element.label : '';
  }

  getElementIcon(type: string | null): string {
    const element = this.courseElementTypeItems.find(el => CourseElementType[el.value] === type);
    return element ? element.icon : '';
  }
}
